import { selectAllMessagesCommand } from 'owa-locstrings/lib/strings/selectallmessagescommand.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47781 */
import { observer } from 'mobx-react';
import { Check } from 'office-ui-fabric-react/lib/components/Check';
import { toggleSelectAllRows } from 'owa-mail-actions/lib/mailListSelectionActions';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import { MailListFilterMenu } from 'owa-mail-list-filter-view';
import { getIsEverythingSelectedInTable, listViewStore } from 'owa-mail-list-store';
import { ActionSource } from 'owa-mail-store';
import * as React from 'react';
import { isFeatureEnabled } from 'owa-feature-flags';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { getIsBitSet, ListViewBitFlagsMasks } from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { getDensityModeString } from 'owa-session-store';
import { isSingleLineListView } from 'owa-mail-layout';
import { isInboxPlusEnabled } from 'owa-inbox-plus-util/lib/util/inboxPlusFlightUtil';

const styles = require('./MailListHeader.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailListHeaderProps {
    filterMenuSource: ActionSource;
    shouldShowFilterMenu: boolean;
    shouldShowSecondRowLeftEntity: boolean;
    tableViewId: string;
    renderFirstRow: (mailListRowCss: string) => JSX.Element;
    renderSecondRowIcon: (iconCss: string) => JSX.Element;
    renderSecondRowCustomContent: (customContainerCss: string) => JSX.Element;
    renderThirdRow: (mailListThirdRowCss: string) => JSX.Element;
    renderBulkActionProgressBar: () => JSX.Element;
    mailListHeaderStylesAsPerUserSettings: string;
}

//  Header Structure
// ---------------------------------------------------------------------------------------------------
// |  Mail List Header Container                                                                     |
// |  ---------------------------------------------------------------------------------------------  |
// |  | First Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Second Row (Half Custom - renders always)                                                 |  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  | |  Check | |           Custom          |                        |     Filter Dropdown    ||  |
// |  | |   Box  | |           Content         |                        |    (based on scenario) ||  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Third Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Bulk action progress bar (Custom - renders optionally)                                    |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// ---------------------------------------------------------------------------------------------------

@observer
export default class MailListHeader extends React.Component<MailListHeaderProps, {}> {
    render() {
        const mailListThirdRowCss = classNames(
            isSingleLineListView()
                ? styles.mailListThirdRowSingleLine
                : styles.mailListThirdRowThreeColumn,
            this.props.mailListHeaderStylesAsPerUserSettings
        );
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const folderId = tableView.tableQuery.folderId;
        const isInboxWithPivots =
            (isInboxPlusEnabled() || isFocusedInboxEnabled()) &&
            folderNameToId('inbox') === folderId;

        const mailListHeaderStyles = this.getMailListHeaderStyles(isInboxWithPivots);

        const isStickyNotesHeader =
            folderId == folderNameToId('notes') && isFeatureEnabled('notes-folder-view');

        if (!this.props.renderSecondRowCustomContent) {
            throw new Error('MailListHeader: renderSecondRowCustomContent should not be null');
        }

        return (
            <div className={mailListHeaderStyles}>
                {this.props.renderFirstRow && this.props.renderFirstRow(styles.mailListFirstRow)}
                <div className={styles.mailListSecondRow}>
                    {this.props.shouldShowSecondRowLeftEntity &&
                        this.renderSecondRowLeftEntity(isInboxWithPivots)}
                    {this.props.renderSecondRowCustomContent(
                        classNames(
                            isStickyNotesHeader
                                ? styles.notesHeaderSecondRow
                                : styles.mailListSecondRowCustomContainer,
                            this.props.mailListHeaderStylesAsPerUserSettings
                        )
                    )}
                    {this.props.shouldShowFilterMenu &&
                        this.renderMailListFilter(isInboxWithPivots)}
                </div>
                {this.props.renderThirdRow && this.props.renderThirdRow(mailListThirdRowCss)}
                {this.props.renderBulkActionProgressBar && this.props.renderBulkActionProgressBar()}
            </div>
        );
    }

    private renderSecondRowLeftEntity = (isInboxWithPivots: boolean) => {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const isListViewEmpty = tableView.rowKeys.length == 0;
        const isInCheckedMode = tableView.isInCheckedMode;
        const isEverythingSelected = getIsEverythingSelectedInTable(tableView);
        const isSingleLineView = isSingleLineListView();
        const isItemView = tableView.tableQuery.listViewType === ReactListViewType.Message;
        const isSenderImageOff = getIsBitSet(ListViewBitFlagsMasks.HideSenderImage);

        const leftEntityClassNames = classNames(
            isSingleLineView && isSenderImageOff
                ? styles.mailListSecondRowLeftEntityNoSenderImage
                : styles.mailListSecondRowLeftEntity,
            isInCheckedMode && styles.showCheckbox,
            !isListViewEmpty && styles.listViewHasItems,
            // Function to check if the user is in SLV and item view, reduce the left margin by 10px, part of density controls work
            !isSingleLineView
                ? styles.leftEntityThreeColumn
                : isItemView
                ? styles.leftEntitySingleLineSLVandItemView
                : styles.leftEntitySingleLine,
            isInboxWithPivots && styles.pivotOffset
        );

        const checkBoxClassNames = classNames(
            styles.checkBox,
            isSingleLineView ? styles.checkBoxSingleLine : styles.checkBoxThreeColumn
        );
        const checkBoxStyles = () => ({
            check: styles.checkStyles,
        });
        const checkBoxLabel = loc(selectAllMessagesCommand);

        return (
            <div
                tabIndex={0}
                role="checkbox"
                title={checkBoxLabel}
                aria-label={checkBoxLabel}
                aria-checked={isEverythingSelected}
                data-selection-toggle={true}
                data-is-focusable={true}
                onKeyDown={this.onCheckKeyDown}
                className={leftEntityClassNames}
                onClick={this.toggleSelectAll}>
                {this.props.renderSecondRowIcon && (
                    <div className={styles.leftEntityIcon}>
                        {this.props.renderSecondRowIcon(styles.secondRowLeftEntityIcon)}
                    </div>
                )}
                {
                    <Check
                        className={checkBoxClassNames}
                        checked={isEverythingSelected}
                        styles={checkBoxStyles}
                    />
                }
            </div>
        );
    };

    private onCheckKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            this.toggleSelectAll();
            evt.preventDefault();
        }
    };

    private toggleSelectAll = () => {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        // If listview is empty, just return
        if (tableView.rowKeys.length === 0) {
            return;
        }

        toggleSelectAllRows(tableView);

        // Must reset focus so that focus goes back to list view so user can perform keyboard triage actions as expected
        lazyResetFocus.importAndExecute();
    };

    private renderMailListFilter = (isInboxWithPivots: boolean) => {
        const viewFilters: ViewFilter[] = [
            'All',
            'Unread',
            'ToOrCcMe',
            'Flagged',
            'Mentioned',
            'HasAttachment',
        ];

        return (
            <MailListFilterMenu
                supportedViewFilters={viewFilters}
                filterMenuSource={this.props.filterMenuSource}
                tableViewId={this.props.tableViewId}
                isInboxWithPivots={isInboxWithPivots}
            />
        );
    };

    private getMailListHeaderStyles = (isInboxWithPivots: boolean) => {
        const mailListHeaderContainerStyles = classNames(
            styles.mailListHeaderContainer,
            isInboxWithPivots && styles.pivotHeader,
            getDensityModeString() === 'compact' && styles.compactHeader
        );

        return classNames(
            this.props.mailListHeaderStylesAsPerUserSettings,
            mailListHeaderContainerStyles
        );
    };
}
